// FI v3 signal tiles: what the Wide-Credit / Rate Trend / Re-arm tiles say, and which engine run they describe.
// docs/HANDOFF_FI_SIGNAL_LINEAGE.md (WI-4, WI-5, WI-7; CIO D2/D4, 2026-09-24).
//
// The Rate Trend tile read "Rising · 4.96% vs 4.55% MA · short / cash" while the 10Y traded ~5.15%. The numbers
// were right (the FRED DGS10 close two sessions back, and its 126-day mean) but the tile never said so, and it
// read "Rising" for ANYTHING that was not literally "falling" — no data and a deadband hold included. Here every
// tile has four explicit states, names the close and source it used, and says when it next updates.

export type RateTileState = "rising" | "falling" | "held" | "unavailable";
export type BinaryTileState = "on" | "off" | "unavailable";
export type LineageStatus = "ok" | "warning" | "problem";

export interface RateTrendSignal {
  direction?: string | null;
  current?: number | null;
  ma?: number | null;
  band_bps?: number | null;
  in_deadband?: boolean | null;
  prior_direction?: string | null;
  available?: boolean | null;
}
export interface WideCreditSignal {
  is_wide?: boolean | null;
  current?: number | null;
  threshold?: number | null;
  current_percentile?: number | null;
  available?: boolean | null;
}
export interface RearmSignal {
  is_tightening?: boolean | null;
  roc_bps?: number | null;
  tighten_bps?: number | null;
  available?: boolean | null;
}
export interface LineageCheck {
  key: string;
  level: LineageStatus;
  note: string;
}
export interface LineageRun {
  run_id?: string | null;
  run_at_utc?: string | null;
  session_date?: string | null;
  code_version?: string | null;
  config_hash?: string | null;
  obs_date_dgs10?: string | null;
  is_hold?: boolean | null;
  inputs_archived?: boolean | null;
  persist_error?: string | null;
}
export interface Lineage {
  source?: "persisted_run" | "live_fetch" | "live_fetch_fallback" | string;
  status?: LineageStatus;
  checks?: LineageCheck[];
  run?: LineageRun | null;
  live_vs_persisted?: { comparable?: boolean; agrees?: boolean | null; differences?: string[] } | null;
}
export interface TapeLine {
  value?: number | null;
  asof_utc?: string | null;
  source?: string | null;
  whatif?: { would_read?: string | null; in_deadband?: boolean; vs_ma_bps?: number | null; prior_direction?: string | null } | null;
}

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const pct = (v: number | null | undefined, dp = 2) => (typeof v === "number" && Number.isFinite(v) ? `${v.toFixed(dp)}%` : "—");

/** "2026-09-22" -> "9/22". */
export function shortDate(iso: string | null | undefined): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? "");
  return m ? `${Number(m[2])}/${Number(m[3])}` : null;
}

/** 14:32 in America/Denver, e.g. "2:32 PM MT". */
export function mountainTime(isoUtc: string | null | undefined): string | null {
  if (!isoUtc) return null;
  const t = Date.parse(isoUtc);
  if (!Number.isFinite(t)) return null;
  return `${new Date(t).toLocaleTimeString("en-US", { timeZone: "America/Denver", hour: "numeric", minute: "2-digit" })} MT`;
}

export interface RateTile {
  state: RateTileState;
  headline: string;
  detail: string;
  /** What the flavor means for positioning. Only an action inside a Defensive state; otherwise a conditional. */
  flavor: string | null;
  asOf: string;
  nextUpdate: string;
}

const FLAVOR: Record<"rising" | "falling", string> = { rising: "short / cash", falling: "hold duration" };

export function rateTrendTile(
  rt: RateTrendSignal | null | undefined,
  opts: { dgs10Last?: string | null; detailedState?: string | null; source?: string | null } = {},
): RateTile {
  const asOf = opts.dgs10Last ? `FRED DGS10 · ${shortDate(opts.dgs10Last)} close` : "FRED DGS10 · close date unknown";
  const nextUpdate =
    opts.source === "persisted_run"
      ? "updates after the 3:15 PM MT engine run"
      : "next official close posts ~2:30 PM MT (H.15, one session behind)";
  const dir = rt?.direction === "rising" || rt?.direction === "falling" ? rt.direction : null;
  if (!rt || rt.available === false || rt.current == null || rt.ma == null || !dir) {
    return {
      state: "unavailable",
      headline: "Unavailable",
      detail: "No full 126-day history for the 10Y — the signal cannot be read.",
      flavor: null,
      asOf,
      nextUpdate,
    };
  }
  const vsMa = (rt.current - rt.ma) * 100;
  const detail = `${pct(rt.current)} vs ${pct(rt.ma)} MA (${vsMa >= 0 ? "+" : ""}${vsMa.toFixed(0)} bp; band ±${rt.band_bps ?? 15} bp)`;
  const defensive = opts.detailedState === "Defensive-RISING" || opts.detailedState === "Defensive-FALLING";
  const flavor = defensive ? `→ ${FLAVOR[dir]}` : `if credit turns wide: ${FLAVOR[dir]}`;
  if (rt.in_deadband) {
    const prior = rt.prior_direction === "rising" || rt.prior_direction === "falling" ? rt.prior_direction : null;
    return {
      state: "held",
      headline: prior ? `Held in deadband (prior: ${cap(prior)})` : `Held in deadband (no prior → ${cap(dir)})`,
      detail,
      flavor,
      asOf,
      nextUpdate,
    };
  }
  return { state: dir, headline: cap(dir), detail, flavor, asOf, nextUpdate };
}

export interface BinaryTile {
  state: BinaryTileState;
  headline: string;
  detail: string;
}

export function wideCreditTile(wc: WideCreditSignal | null | undefined): BinaryTile {
  if (!wc || wc.available === false || wc.current == null || wc.threshold == null) {
    return { state: "unavailable", headline: "Unavailable", detail: "Not enough Baa−10y history to set the gate." };
  }
  const wide = Boolean(wc.is_wide);
  return {
    state: wide ? "on" : "off",
    headline: wide ? "WIDE" : "Not wide",
    detail: `Baa−10y ${pct(wc.current)} ${wide ? "≥" : "<"} p80 ${pct(wc.threshold)}`,
  };
}

export function rearmTile(ra: RearmSignal | null | undefined): BinaryTile {
  if (!ra || ra.available === false || ra.roc_bps == null) {
    return { state: "unavailable", headline: "Re-arm watch unavailable", detail: "Not enough Baa−10y history for the 42-day change." };
  }
  const t = Boolean(ra.is_tightening);
  const roc = ra.roc_bps;
  return {
    state: t ? "on" : "off",
    headline: t ? "Tightening (re-arm armed)" : "Not tightening",
    detail: `Baa−10y ${roc >= 0 ? "+" : ""}${roc.toFixed(1)} bp / 42d (arms at −${ra.tighten_bps ?? 15} bp)`,
  };
}

export interface LineageView {
  status: LineageStatus | "unknown";
  /** One line under the tiles: which computation the tiles show. */
  summary: string;
  /** Non-ok checks, worst first. */
  issues: LineageCheck[];
  /** Hover text: run id, config hash, code version. */
  tooltip: string;
}

export function lineageView(lin: Lineage | null | undefined): LineageView {
  if (!lin) {
    return { status: "unknown", summary: "Signal source not reported by this backend version.", issues: [], tooltip: "" };
  }
  const run = lin.run ?? null;
  const when = run?.run_at_utc ? `${shortDate(run.session_date ?? run.run_at_utc)} ${mountainTime(run.run_at_utc) ?? ""}`.trim() : null;
  let summary: string;
  if (lin.source === "persisted_run") {
    summary = when ? `Signals as recorded by the engine run ${when}.` : "Signals as recorded by the engine run.";
  } else if (lin.source === "live_fetch_fallback") {
    summary = "Signals fetched live from FRED — no readable engine run record, so the recorded view is unavailable.";
  } else {
    const cmp = lin.live_vs_persisted;
    const agree =
      cmp && cmp.comparable
        ? cmp.agrees
          ? `; the ${when ?? "latest"} engine run recorded the same signals`
          : `; DISAGREES with the ${when ?? "latest"} engine run on ${(cmp.differences ?? []).join(", ")}`
        : when
          ? `; latest engine run ${when} used an older close`
          : "";
    summary = `Signals fetched live from FRED${agree}.`;
  }
  const order: Record<string, number> = { problem: 0, warning: 1, ok: 2 };
  const issues = (lin.checks ?? []).filter((c) => c.level !== "ok").sort((a, b) => order[a.level] - order[b.level]);
  const tooltip = run
    ? [
        `Run ${run.run_id ?? "—"}`,
        `Config ${run.config_hash ? run.config_hash.slice(0, 12) : "—"}`,
        `Code ${run.code_version ?? "unstamped"}`,
        run.inputs_archived ? "Raw inputs archived" : "Raw inputs NOT archived",
      ].join(" · ")
    : "No engine run recorded";
  return { status: lin.status ?? "unknown", summary, issues, tooltip };
}

/** CIO D4: the IBKR indicative line. Null (render nothing) when the backend sent none. */
export function tapeLineText(tape: TapeLine | null | undefined): string | null {
  if (!tape || typeof tape.value !== "number" || !Number.isFinite(tape.value)) return null;
  const at = mountainTime(tape.asof_utc);
  const w = tape.whatif;
  let what = "";
  if (w) {
    const bp = typeof w.vs_ma_bps === "number" ? ` (${w.vs_ma_bps >= 0 ? "+" : ""}${w.vs_ma_bps.toFixed(0)} bp vs MA)` : "";
    if (w.in_deadband) {
      what = w.would_read
        ? ` — inside the band${bp}: the signal would hold ${cap(w.would_read)}`
        : ` — inside the band${bp}: no prior to hold`;
    } else if (w.would_read) {
      what = ` — at this level the signal would read ${cap(w.would_read)}${bp}`;
    }
  }
  return `Tape ${tape.value.toFixed(2)}% (${tape.source ?? "IBKR"}${at ? `, ${at}` : ""})${what}. Indicative only; does not feed the signal.`;
}
